// Create a new file: src/sections/@dashboard/settings/ZapierIntegration.js

import {
    Box,
    Card,
    Link,
    Stack,
    Divider,
    Typography,
    CardHeader,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
  } from '@mui/material';
  import Iconify from '../../../components/iconify';
  
  export default function ZapierIntegration() {
    return (
      <Card>
        <CardHeader title="Zapier Integration" />
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="body1">
              Connect FlowBookings with 3,000+ apps through Zapier. Automate your workflow by creating custom Zaps
              that trigger when new bookings are created.
            </Typography>
  
            <Box>
              <Typography variant="h6" gutterBottom>
                Popular Integrations
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Iconify icon="logos:google-calendar" width={24} />
                  </ListItemIcon>
                  <ListItemText primary="Google Calendar" secondary="Add bookings to your Google Calendar automatically" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Iconify icon="logos:slack-icon" width={24} />
                  </ListItemIcon>
                  <ListItemText primary="Slack" secondary="Get notifications in Slack when new bookings are made" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Iconify icon="logos:trello" width={24} />
                  </ListItemIcon>
                  <ListItemText primary="Trello" secondary="Create cards for new bookings" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Iconify icon="logos:mailchimp-icon" width={24} />
                  </ListItemIcon>
                  <ListItemText primary="Mailchimp" secondary="Add customers to your email lists" />
                </ListItem>
              </List>
            </Box>
  
            <Divider />
  
            <Typography variant="h6">How to Connect with Zapier</Typography>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Step 1: Generate Your API Key
              </Typography>
              <Typography variant="body2" paragraph>
                Generate an API key from the "API Key Management" section above. You'll need this key to authenticate with Zapier.
              </Typography>
  
              <Typography variant="subtitle1" gutterBottom>
                Step 2: Create a Zap in Zapier
              </Typography>
              <Typography variant="body2" paragraph>
                - Log in to your Zapier account and click "Create Zap"
                <br />
                - Search for "FlowBookings" in the app directory
                <br />
                - Select "New Booking" as your trigger event
                <br />
                - When prompted, enter your API key and the FlowBookings API base URL:
                <Typography 
                  component="div" 
                  sx={{ 
                    bgcolor: 'background.neutral',
                    p: 1.5,
                    mt: 1,
                    mb: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace'
                  }}
                >
                  https://api.flowbookings.io/api/zapier
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Available Triggers:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" component="div">
                    <strong>New Booking</strong> - Triggers when a new booking is created
                    <br />
                    <em>Endpoint:</em> GET /bookings
                  </Typography>
                </Box>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Real-time Webhooks:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    FlowBookings supports real-time webhooks for instant notifications when new bookings are created. 
                    Zapier will automatically configure this for you when you set up the FlowBookings trigger.
                  </Typography>
                </Box>
              </Typography>
  
              <Typography variant="subtitle1" gutterBottom>
                Step 3: Test & Configure Your Zap
              </Typography>
              <Typography variant="body2" paragraph>
                - Test the connection to make sure it's working properly
                <br />
                - Configure the action app (e.g., Google Calendar, Slack, etc.)
                <br />
                - Turn on your Zap and it will start running automatically
              </Typography>
            </Box>
  
            <Divider />
            
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Example Integrations
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Google Calendar Integration
              </Typography>
              <Typography variant="body2" paragraph>
                Automatically add new bookings to your Google Calendar.
              </Typography>
              <Link 
                href="/examples/flowbookings-google-calendar.js" 
                download="flowbookings-google-calendar.js"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <Iconify icon="eva:download-outline" sx={{ mr: 0.5 }} />
                Download Google Calendar Example
              </Link>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Zapier Integration Definition
              </Typography>
              <Typography variant="body2" paragraph>
                For developers: Custom Zapier integration definition file.
              </Typography>
              <Link 
                href="/examples/flowbookings-zapier-definition.js" 
                download="flowbookings-zapier-definition.js"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <Iconify icon="eva:download-outline" sx={{ mr: 0.5 }} />
                Download Zapier Definition
              </Link>
            </Box>
  
            <Divider />
            
            <Typography variant="body2">
              Need more help? Visit our{' '}
              <Link href="https://www.flowbookings.io/support" target="_blank" rel="noopener">
                support documentation
              </Link>{' '}
              or contact our support team.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }